import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Button, Paper, Typography, Link } from '@mui/material';
import { useForm } from 'react-hook-form';
import validationUtils from '../../services/validationUtils';
import TextInput2 from '../form/TextInput2';
import { useNavigate } from 'react-router-dom';

const PREFIX = 'ForgotPasswordForm';

const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  error: `${PREFIX}-error`,
  submit: `${PREFIX}-submit`,
  link: `${PREFIX}-link`,
};

const Root = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    width: 305,
    padding: 32,
  },

  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(4),
  },

  [`& .${classes.title}`]: {
    fontSize: 21,
    fontFamily: 'Bitter',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.subtitle}`]: {
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.error}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.submit}`]: {
    marginTop: theme.spacing(4),
  },

  [`& .${classes.link}`]: {
    display: 'block',
  },
}));

interface ForgotPasswordFormProps {
  error?: string;
  submit: (info: any) => void;
}

export default function ForgotPasswordForm({
  error,
  submit,
}: ForgotPasswordFormProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const requiredFieldMessage = t('common.requiredField');
  const { control, formState, handleSubmit } = useForm({
    defaultValues: {
      Email: '',
    } as any,
  });
  const [passwordVisible, setPasswordVisible] = React.useState(false);

  return (
    <Root className={classes.container}>
      <Typography className={classes.title}>
        {t('login.forgotPasswordTitle')}
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        {t('login.resetDescription')}
      </Typography>
      <form onSubmit={handleSubmit(submit)} className={classes.form}>
        <TextInput2
          label={t('login.username')}
          name={'Email'}
          required={true}
          control={control}
          errors={formState.errors}
          rules={{
            required: requiredFieldMessage,
            pattern: validationUtils.emailValidation(),
          }}
        />
        <Typography color="error" className={classes.error}>
          {error}
        </Typography>
        <Button
          color="primary"
          variant="contained"
          className={classes.submit}
          type="submit">
          {t('login.requestPasswordReset')}
        </Button>
      </form>
      <Link
        href="#"
        onClick={(evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          evt.preventDefault();
          navigate('/login');
        }}
        color="primary"
        underline="hover"
        align="center"
        className={classes.link}>
        {t('login.backToLogin')}
      </Link>
    </Root>
  );
}
